import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'lokos-select',
    templateUrl: './select.component.html',
    styleUrl: './select.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class SelectComponent implements OnChanges, OnInit {
    @Input({ required: false }) control: FormControl = new FormControl();
    @Input({ required: false }) countField = '';
    @Input({ required: false }) disabled = false;
    @Input({ required: false }) displayName = '';
    @Input({ required: false }) getName = '';
    @Input({ required: false }) label = '';
    @Input({ required: false }) multiSelect = false;
    @Input({ required: false }) value = '';
    @Input({ required: true }) optionsList: any[] = [];
    @Input({ required: true }) placeholder = 'placeholder';
    @Input({ required: true }) required = false;
    @Output() afterSelectionChange = new EventEmitter();

    allSelected = false;
    indeterminate = false;
    private internalValue: any[] = [];

    constructor(
        private translate: TranslateService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.control.statusChanges.subscribe(() => {
            this.cdr.markForCheck();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['disabled']) {
            if (this.disabled) {
                this.control.disable({ emitEvent: false });
            } else {
                this.control.enable({ emitEvent: false });
            }
        }
        if (changes['value']) {
            this.setInternalValue(this.value);
            this.control.setValue(this.value, { emitEvent: false });
            this.updateSelectAllState();
        }
        if (changes['optionsList'] || changes['getName']) {
            // Re-apply value when options arrive (value may have been set before options were available)
            if (this.value && this.optionsList?.length) {
                this.control.setValue(this.value, { emitEvent: false });
            }
            this.updateSelectAllState();
        }
    }

    onSelectionChange(event: MatSelectChange) {
        if (!this.multiSelect) {
            this.afterSelectionChange.emit(event);
            return;
        }

        let selectedValues = [...event.value];
        const allOptionValues = this.getAllOptionValues();

        const selectAllClicked = selectedValues.includes('selectAll');

        if (selectAllClicked) {
            if (this.allSelected) {
                selectedValues = [];
            } else {
                selectedValues = [...allOptionValues];
            }
        } else {
            selectedValues = selectedValues.filter(
                (val) => val !== 'selectAll'
            );
        }

        this.setInternalValue(selectedValues);

        this.control.setValue(selectedValues, { emitEvent: false });

        this.updateSelectAllState(selectedValues);

        this.afterSelectionChange.emit({
            source: event.source,
            value: selectedValues,
            displayValue: selectedValues.join(', '),
        });
    }

    private setInternalValue(value: any) {
        if (Array.isArray(value)) {
            this.internalValue = value;
        } else if (typeof value === 'string' && value.trim() !== '') {
            this.internalValue = value.split(',').map((v) => v.trim());
        } else {
            this.internalValue = [];
        }
    }

    private getAllOptionValues(): any[] {
        return this.optionsList.map((option) =>
            this.getName ? option[this.getName] : option
        );
    }

    private updateSelectAllState(selectedValues: any[] = []) {
        if (!this.multiSelect) return;

        const allOptionValues = this.getAllOptionValues();
        const selectedCount = allOptionValues.filter((val) =>
            selectedValues.includes(val)
        ).length;

        this.allSelected =
            selectedCount === allOptionValues.length &&
            allOptionValues.length > 0;
        this.indeterminate =
            selectedCount > 0 && selectedCount < allOptionValues.length;
    }

    getErrorMessage() {
        const label = this.label
            ? this.translate.instant(this.label)
            : this.translate.instant('thisField');

        if (this.control.hasError('required')) {
            return `${label} ${this.translate.instant('isRequired')}`;
        }
        return '';
    }

    getSelectedDisplay(): string {
        const value = this.control?.value;
        if (value === null || value === undefined || value === '') return '';
        const selected = this.optionsList?.find((o: any) =>
            this.getName ? o[this.getName] === value : o === value
        );
        if (!selected) return '';
        const display = this.displayName ? selected[this.displayName] : selected;
        return this.translate.instant(display);
    }
}
